import { React, useEffect, useRef, useState } from 'react';

// mui
import {
    TableContainer,
    Table,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    Tooltip,
    Stack,
    Typography,
    Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// components
import { getUserStatusString, getUserBlockReasonString } from '../..';
import { def } from '@goldblack/shared';

// api
import { AdminUserProfile } from '../../../api';

// client-shared
import { FilterDesc, MarkDown, ContainerScroll } from '@goldblack/client-comps';

// local
import { ProfileImages } from './profile-images.jsx';
import { PostViewAdmin } from './post-view.jsx';

function fmtDate(val) {
    if (!val) return '';
    const d = typeof val === 'number' ? new Date(val) : new Date(Date.parse(val));
    return isNaN(d) ? String(val) : d.toLocaleString('ko-KR');
}

function LoiseUserBlocked(props) {
    const [dateString, setDateString] = useState('');
    const [expireString, setExpireString] = useState('');
    const [reasonString, setReasonString] = useState('');

    useEffect(() => {
        if (props.blocked) {
            setDateString(fmtDate(props.blocked.date));
            setExpireString(fmtDate(props.blocked.expire));
            setReasonString(getUserBlockReasonString(props.blocked.reason));
        } else {
            setDateString('—');
            setExpireString('—');
            setReasonString('—');
        }
    }, [props.blocked]);

    return (
        <TableContainer>
            <Table padding='checkbox'>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>date</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            {dateString}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>expire</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {expireString}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>reason</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {reasonString}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LoiseUserInactive(props) {
    const [dateString, setDateString] = useState('');
    const [forcedString, setForcedString] = useState('');
    const [reasonString, setReasonString] = useState('');

    useEffect(() => {
        if (props.inactive) {
            setDateString(fmtDate(props.inactive.date));
            setForcedString(props.inactive.forced ? 'true' : 'false');
            setReasonString(getUserBlockReasonString(props.inactive.reason));
        } else {
            setDateString('—');
            setForcedString('—');
            setReasonString('—');
        }
    }, [props.inactive]);

    return (
        <TableContainer>
            <Table padding='checkbox'>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>date</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            {dateString}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>forced</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {forcedString}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>reason</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {reasonString}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function CensoredCommentList({ comments }) {
    if (!comments?.length) return null;
    return (
        <Stack spacing={0.5} sx={{ mt: 0.5, textAlign: 'left' }}>
            {comments.map((c) => (
                <Stack key={c.id} spacing={0}>
                    <Typography variant='caption' color='text.secondary'>
                        {fmtDate(c.date)}
                    </Typography>
                    <Typography variant='caption' sx={{ wordBreak: 'break-all' }}>
                        {c.content}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

function UserNsfw({ nsfw }) {
    const level = nsfw?.level ?? 0;
    const expiresAt = nsfw?.expires_at ?? null;
    const levelExpiresAt = nsfw?.level_expires_at ?? null;
    const lastCensoredAt = nsfw?.last_censored_at ?? null;
    const now = Date.now();
    const active = expiresAt && expiresAt > now;
    const levelActive = level > 0 && (!levelExpiresAt || levelExpiresAt > now);

    let state = 'none';
    if (level === 1 && active) state = 'warning';
    else if (level >= 2 && active) state = 'banned';

    return (
        <TableContainer>
            <Table padding='checkbox'>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>level</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            {levelActive ? (
                                <Chip
                                    label={`${level} · ${state}`}
                                    size='small'
                                    color={state === 'banned' ? 'error' : state === 'warning' ? 'warning' : 'default'}
                                    sx={{ height: 16, fontSize: 10 }}
                                />
                            ) : (
                                '—'
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>ban expire</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {expiresAt ? fmtDate(expiresAt) : '—'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>level expire</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {levelExpiresAt ? fmtDate(levelExpiresAt) : '—'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>last censored</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {lastCensoredAt ? fmtDate(lastCensoredAt) : '—'}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LoiseUserSubscription({ subscription: sub }) {
    return (
        <TableContainer>
            <Table padding='checkbox'>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>status</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            {sub?.status ? (
                                <Chip
                                    label={sub.status}
                                    size='small'
                                    color={sub.status === 'active' ? 'success' : 'default'}
                                    sx={{ height: 16, fontSize: 10 }}
                                />
                            ) : (
                                '—'
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>plan</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {sub?.plan ?? '—'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>expires</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {fmtDate(sub?.expires_at) || '—'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>updated</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {fmtDate(sub?.updated_at) || '—'}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function UserLegal({ legal, pending = [] }) {
    return (
        <TableContainer>
            <Table padding='checkbox'>
                <TableBody>
                    {['privacy', 'terms', 'notice'].map((type) => (
                        <TableRow key={type}>
                            <TableCell sx={{ width: '30%', borderBottom: 'none' }}>{type}</TableCell>
                            <TableCell align='right' sx={{ borderBottom: 'none' }}>
                                {pending.includes(type) ? (
                                    <Chip
                                        label='pending unset'
                                        color='warning'
                                        size='small'
                                        sx={{ height: 16, fontSize: 10 }}
                                    />
                                ) : legal?.[type] ? (
                                    [`v${legal[type].version}`, fmtDate(legal[type].agreedAt)]
                                        .filter(Boolean)
                                        .join(' · ')
                                ) : (
                                    '—'
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function UserPreferences({ preferences: pref }) {
    const fmt = (v) => (v ? `dim=${v.dim} norm=${v.norm ?? '—'}` : '—');
    return (
        <TableContainer>
            <Table padding='checkbox'>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '30%', borderBottom: 'none' }}>favorites</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {fmt(pref?.favorites)}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>following</TableCell>
                        <TableCell align='right' sx={{ borderBottom: 'none' }}>
                            {fmt(pref?.following)}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LoiseUserInfoPanel(props) {
    const [data, setData] = useState({
        username: '',
        image: '',
        thumbnail: '',
        filterDesc: null,
        profileDesc: null,
        subscription: null,
        badges: null,
        lastLoginAt: undefined,
        blocked: null,
        inactive: null,
        restrictionPost: null,
        restrictionComment: null,
        restrictionMessage: null,
        censoredComments: [],
        preferences: null,
        legal: null,
        posts: [],
    });
    const [createdAtString, setCreatedAtString] = useState('');
    const [updatedAtString, setUpdatedAtString] = useState('');
    const [openImage, setOpenImage] = useState(false);
    const [post, setPost] = useState(null);
    function onClickImage(event, index, item) {
        if (item) {
            setOpenImage(true);
            setPost(item);
        }
    }

    const onCloseImage = () => {
        setOpenImage(false);
    };

    const onStatusChange = (updatedPost) => {
        setPost(updatedPost);
        setData((prev) => ({
            ...prev,
            posts: prev.posts.map((p) => (p && p._id === updatedPost._id ? { ...p, ...updatedPost } : p)),
        }));
    };

    const profileGenRef = useRef(0);

    useEffect(() => {
        if (props.user) {
            setCreatedAtString(fmtDate(props.user.createdAt));
            setUpdatedAtString(fmtDate(props.user.updatedAt));

            const gen = ++profileGenRef.current;
            AdminUserProfile('loise', props.user._id, (updater) => {
                if (profileGenRef.current === gen) setData(updater);
            });
        }
    }, [props.user]);

    return (
        <ContainerScroll>
            <TableContainer sx={{ overflowX: 'hidden' }}>
                <Table
                    sx={{ tableLayout: 'fixed', width: '100%', '& td, & th': { border: 1, borderColor: 'divider' } }}>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ width: '20%' }}>userId</TableCell>
                            <TableCell sx={{ width: '80%' }} align='right'>
                                {props.user?._id}
                                <Tooltip title='copy to clipboard'>
                                    <IconButton
                                        size='small'
                                        sx={{ ml: 0.5 }}
                                        onClick={() => navigator.clipboard.writeText(props.user?._id ?? '')}>
                                        <ContentCopyIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>lastLoginAt</TableCell>
                            <TableCell align='right'>{data.lastLoginAt ? fmtDate(data.lastLoginAt) : '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>status</TableCell>
                            <TableCell align='right'>
                                {props.user?.status !== undefined ? (
                                    <Chip
                                        label={getUserStatusString(props.user.status)}
                                        size='small'
                                        color={
                                            props.user.status === def.UserStatus.ACTIVE
                                                ? 'success'
                                                : props.user.status === def.UserStatus.BLOCKED
                                                  ? 'error'
                                                  : props.user.status === def.UserStatus.NOT_VERIFIED
                                                    ? 'warning'
                                                    : props.user.status === def.UserStatus.INVISIBLE
                                                      ? 'info'
                                                      : 'default'
                                        }
                                        sx={{ height: 16, fontSize: 10 }}
                                    />
                                ) : (
                                    '—'
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>type</TableCell>
                            <TableCell align='right'>
                                {props.user?.type === def.UserType.ADMIN ? 'Admin' : 'User'}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ verticalAlign: 'top' }}>subscription</TableCell>
                            <TableCell align='right'>
                                <LoiseUserSubscription subscription={data.subscription} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>badges</TableCell>
                            <TableCell align='right'>
                                {(() => {
                                    const b = props.pendingBadges
                                        ? { ...data.badges, ...props.pendingBadges }
                                        : data.badges;
                                    return (
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            justifyContent='flex-end'
                                            spacing={0.5}>
                                            {b?.verified ? (
                                                <Chip
                                                    label='verified'
                                                    size='small'
                                                    color='info'
                                                    sx={{ height: 16, fontSize: 10 }}
                                                />
                                            ) : null}
                                            {b?.membership ? (
                                                <Chip
                                                    label='membership'
                                                    size='small'
                                                    color='secondary'
                                                    sx={{ height: 16, fontSize: 10 }}
                                                />
                                            ) : null}
                                            {!b?.verified && !b?.membership && '—'}
                                            {props.pendingBadges && (
                                                <Chip
                                                    label='pending'
                                                    size='small'
                                                    color='warning'
                                                    sx={{ height: 16, fontSize: 10 }}
                                                />
                                            )}
                                        </Stack>
                                    );
                                })()}
                            </TableCell>
                        </TableRow>
                        {props.user && (
                            <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                    <ProfileImages
                                        posts={data.posts.length === 10 ? data.posts : null}
                                        loading={data.posts.length !== 10}
                                        onClick={onClickImage}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell>filterdesc</TableCell>
                            <TableCell align='right'>
                                <FilterDesc filterDesc={data.filterDesc} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>profiledesc</TableCell>
                            <TableCell>
                                <MarkDown src={data.profileDesc} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ verticalAlign: 'top' }}>user_vectors</TableCell>
                            <TableCell align='right'>
                                <UserPreferences preferences={data.preferences} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ verticalAlign: 'top' }}>legal</TableCell>
                            <TableCell align='right'>
                                <UserLegal legal={data.legal} pending={props.pendingLegal} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>blocked</TableCell>
                            <TableCell align='right'>
                                <LoiseUserBlocked blocked={data.blocked} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>inactive</TableCell>
                            <TableCell align='right'>
                                <LoiseUserInactive inactive={data.inactive} />
                            </TableCell>
                        </TableRow>
                        {['post', 'comment', 'message'].map((rtype) => {
                            const pr = props.pendingRestriction ?? {};
                            const rkey = `restriction${rtype.charAt(0).toUpperCase() + rtype.slice(1)}`;
                            const val = rtype in pr ? pr[rtype] : data[rkey];
                            return (
                                <TableRow key={rtype}>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        restriction
                                        <br />({rtype})
                                    </TableCell>
                                    <TableCell align='right'>
                                        {typeof val === 'boolean' ? (
                                            <Chip
                                                label={val ? 'pending set' : 'pending unset'}
                                                color='warning'
                                                size='small'
                                                sx={{ height: 16, fontSize: 10 }}
                                            />
                                        ) : (
                                            <UserNsfw nsfw={val} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        <TableRow>
                            <TableCell>createdAt</TableCell>
                            <TableCell align='right'>{createdAtString}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>updatedAt</TableCell>
                            <TableCell align='right'>{updatedAtString}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <PostViewAdmin
                open={openImage}
                onClose={onCloseImage}
                post={post}
                user={{ username: data.username, image: data.image, thumbnail: data.thumbnail, badges: data.badges }}
                onStatusChange={onStatusChange}
            />
        </ContainerScroll>
    );
}

export { LoiseUserInfoPanel };
